import { ConstructorArgs } from '../types/models'
import { Alias } from './Alias'
import { BaseRecord } from './BaseRecord'
import { FIELD_TYPE } from './Field'
import { Industry } from './Industry'

type LeaderboardConstructorArgs = ConstructorArgs<() => Leaderboard> & { data_type: string | null }

export enum LEADERBOARD_TYPE {
    LEADERBOARD_TYPE_INCOME, // income
    LEADERBOARD_TYPE_PRICE, // top selling service price
    LEADERBOARD_TYPE_RATE, // avg hourly rate
    LEADERBOARD_TYPE_USAGE, // % utilization
    LEADERBOARD_TYPE_CLIENT, // # paying clients
    LEADERBOARD_TYPE_LEAD, // # online leads
    LEADERBOARD_TYPE_PREPAID, // % secured appointments
    LEADERBOARD_TYPE_REPEAT, // % repeat clients
}

export class Leaderboard extends BaseRecord {
    readonly recordType = 'leaderboard'

    count: number = 0
    rank: number = 0
    top?: number
    mean?: number
    mine?: number
    median?: number
    title?: string
    dateType?: string
    fieldType?: FIELD_TYPE

    constructor(data?: Partial<LeaderboardConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.count = this.propertyFromIntArg(data.count) ?? 0
        this.rank = this.propertyFromIntArg(data.rank) ?? 0
        this.top = this.propertyFromCurrencyArg(data.top)
        this.mean = this.propertyFromCurrencyArg(data.mean)
        this.mine = this.propertyFromCurrencyArg(data.mine)
        this.median = this.propertyFromCurrencyArg(data.median)
        this.title = data.title ?? undefined
        this.dateType = data.date_type ?? undefined
        this.fieldType = (() => {
            if (data.data_type === 'integer') return FIELD_TYPE.FIELD_TYPE_INTEGER
            else if (data.data_type === 'percent') return FIELD_TYPE.FIELD_TYPE_PERCENT
            return FIELD_TYPE.FIELD_TYPE_CURRENCY
        })()
    }

    clone(overrides?: Partial<LeaderboardConstructorArgs>): Leaderboard {
        const dataType = (() => {
            switch (this.fieldType) {
                case FIELD_TYPE.FIELD_TYPE_INTEGER: {
                    return 'integer'
                }
                case FIELD_TYPE.FIELD_TYPE_PERCENT: {
                    return 'percent'
                }
                default:
                    return ''
            }
        })()

        return new Leaderboard({ ...this.serializeForClone(), data_type: dataType, ...overrides })
    }

    get type(): LEADERBOARD_TYPE | undefined {
        if (this.id === 'income' || this.id === 'income_ytd')
            return LEADERBOARD_TYPE.LEADERBOARD_TYPE_INCOME
        else if (this.id === 'price') return LEADERBOARD_TYPE.LEADERBOARD_TYPE_PRICE
        else if (this.id === 'rate') return LEADERBOARD_TYPE.LEADERBOARD_TYPE_RATE
        else if (this.id === 'usage') return LEADERBOARD_TYPE.LEADERBOARD_TYPE_USAGE
        else if (this.id === 'client') return LEADERBOARD_TYPE.LEADERBOARD_TYPE_CLIENT
        else if (this.id === 'lead') return LEADERBOARD_TYPE.LEADERBOARD_TYPE_LEAD
        else if (this.id === 'prepaid') return LEADERBOARD_TYPE.LEADERBOARD_TYPE_PREPAID
        else if (this.id === 'repeat') return LEADERBOARD_TYPE.LEADERBOARD_TYPE_REPEAT
        return undefined
    }

    get alias(): Alias | undefined {
        return this.getForeignRecord('alias', Alias)
    }
    set alias(alias: Alias | undefined) {
        this.setForeignRecord('alias', alias)
    }
    get channel(): Industry | undefined {
        return this.getForeignRecord('channel', Industry)
    }
    set channel(channel: Industry | undefined) {
        this.setForeignRecord('channel', channel)
    }
}
